import { push, replace } from 'connected-react-router';
import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { actions as jobModuleActions } from '@saucelabs/sl-module-jobs';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { scrollToElementIfNotVisible } from '@saucelabs/sl-util-helpers';
import { selectors as userSelectors } from '@saucelabs/sl-module-user';

import { filteredCommandsSelector, location } from '../container/selectors';
import api from './api';
import * as actions from './actions';
import * as types from './types';
import * as containerConstants from '../container/constants';
import { functions as webdriverCommandFunctions } from './../../webdriver-commands';
import { updateTimestamp } from './../video/slice';
import * as jobSelectors from './selectors';

export const changeJobVisibility = ({ id, visibility, username }) => async (dispatch) => {
  try {
    await dispatch(jobModuleActions.changeVisibility({ id, visibility, username }));
    dispatch(notificationActions.notifySuccess('Visibility changed', undefined, false, undefined, true));
  } catch (err) {
    dispatch(notificationActions.notifyDanger('We were not able to change job visibility', undefined, false, undefined, true));
  }
};

export const deleteJob = ({ id, username }) => (dispatch) => (
  dispatch(jobModuleActions.deleteJob({ id, username }))
    .then(() => dispatch(push('/dashboard/tests')))
    .catch(() => dispatch(notificationActions.notifyDanger(
      'We were not able to delete the job',
    )))
);

export const deleteJobRdc = ({ id }) => (dispatch) => (
  dispatch(jobModuleActions.deleteJobRdc({ id }))
    .then(() => dispatch(push('/dashboard/tests')))
    .catch(() => dispatch(notificationActions.notifyDanger(
      'We were not able to delete the job',
    )))
);

export const breakpointJob = ({ id, username }) => (dispatch) => (
  dispatch(jobModuleActions.breakpointJob({ id, username }))
);

export const stopJob = ({ id, username }) => (dispatch) => (
  dispatch(jobModuleActions.stopJob({ id, username }))
);

export const stopJobRdc = ({ id }) => async (dispatch) => {
  dispatch(jobModuleActions.stopJobRdc({ id }))
    .catch((error) => {
      const defaultMessage = 'Session could not be stopped';
      const message = error.response.data?.message || defaultMessage;
      dispatch(notificationActions.notifyError(message));
    });
};

export const updateActiveCommand = (payload) => (dispatch, getState) => {
  const {
    collapseCurrent,
    commandId,
    currentTime,
    timestamp,
    updateScrollTime = true,
  } = payload;

  const allCommands = jobSelectors.selectCommands(getState());
  const routerLocation = location(getState());

  // Update TestCases for batch frameworks.
  if (allCommands.has('testsuites')) {
    const testcases = allCommands.get('testsuites')?.flatMap((suite) => suite.get('testcases'));

    let testNumber;
    if (Number.isFinite(currentTime)) {
      let idx = testcases.findIndex((tc) => tc.get('video_timestamp') > currentTime);
      if (idx === -1) {
        idx = testcases.size;
      }
      if (idx > 0) {
        idx -= 1;
      }

      testNumber = testcases.getIn([idx, 'test_number']);
    }

    if (Number.isFinite(commandId)) {
      testNumber = commandId;
    }

    if (testNumber) {
      dispatch(replace({ ...routerLocation, hash: `#${testNumber}` }));
    }
    return;
  }

  // It's possible that active command is currently filtered out. We should still deactivate it.
  const oldActiveId = jobSelectors.selectActiveCommand(getState())?.get('id') ?? -1;
  const filteredCommands = filteredCommandsSelector(getState());
  const activeLeftPanelTab = getState().apps.job.files.get('activeLeftPanelTab');

  let newActiveId;
  if (Number.isFinite(currentTime)) {
    const activeCommand = webdriverCommandFunctions.findActiveCommand({
      commands: filteredCommands,
      currentTime,
    });
    if (activeCommand) {
      newActiveId = activeCommand.id;
    }
  }
  if (Number.isFinite(commandId)) {
    newActiveId = commandId;
  }
  if (timestamp) {
    dispatch(updateTimestamp(Math.trunc(timestamp)));
  }

  if (Number.isFinite(newActiveId) && newActiveId !== oldActiveId) {
    dispatch(replace({ ...routerLocation, hash: `#${newActiveId}` }));
    dispatch(actions.updateActiveCommand({
      collapseCurrent, newActiveId, oldActiveId, updateScrollTime,
    }));

    if (activeLeftPanelTab === containerConstants.TEST_DETAILS_TAB_COMMANDS) {
      scrollToElementIfNotVisible(
        `command-${newActiveId}`,
        containerConstants.COMMAND_LIST_CONTAINER_ID,
        { duration: 100, smooth: true },
      );
    }
  }
};

export const updateTestDetailsForJob = createAsyncThunk(
  types.UPDATE_JOB_DETAILS,
  async ({ job, testDetails }, { dispatch, getState, rejectWithValue }) => {
    const currentUserID = userSelectors.getCurrentUserID(getState());
    const newTestDetails = {
      id: job.get('id'),
      ...testDetails,
    };

    // Sending null as passed state will result in a "failed" state at least on VDC jobs,
    // so it is deleted for this case.
    if (newTestDetails.passed === null) {
      delete newTestDetails.passed;
    }

    try {
      const apiResponse = job.isRealDevice() ?
        await api.updateRealDeviceJob(newTestDetails) :
        await api.updateVirtualJob(currentUserID, newTestDetails);

      dispatch(notificationActions.notifySuccess(
        'You have successfully updated your test details.', undefined, false, undefined, true,
      ));

      return apiResponse.data;
    } catch (error) {
      dispatch(notificationActions.notifyDanger(
        'We were unable to update the test details.', undefined, false, undefined, true,
      ));

      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          code: error.response?.status,
          detail: error.response?.data,
          title: error.response?.statusText,
        });
      }

      return rejectWithValue({
        code: 500,
        detail: error.message,
        title: error.name,
      });
    }
  },
);
